<template>
  <b-card no-body class="dash-panel dash-att-panel dash-att-panel--glass">
    <b-card-header class="dash-panel__header dash-att-panel__header border-0">
      <div class="dash-att-panel__top">
        <div class="dash-panel__heading">
          <span class="dash-panel__icon dash-panel__icon--att">
            <feather-icon icon="UsersIcon" size="18" />
          </span>
          <div>
            <b-card-title class="dash-panel__title mb-0">{{
              $t("Student Attendance")
            }}</b-card-title>
            <small class="dash-panel__subtitle">{{
              $t("Class-wise summary")
            }}</small>
          </div>
        </div>
        <div class="dash-att-panel__actions">
          <label class="dash-att-date">
            <feather-icon icon="CalendarIcon" size="15" />
        <flat-pickr
          v-model="attDate"
          :config="config2"
              class="form-control flat-picker dash-att-date__input"
          :placeholder="$t('Select Date')"
          @on-change="LoadData()"
        />
          </label>
          <b-button
            variant="outline-primary"
            class="dash-att-export"
            v-b-tooltip.hover.top
            :title="$t('Export')"
            @click="exportData()"
            :disabled="attLoading || exporting"
          >
            <b-spinner v-if="exporting" small class="mr-50" />
            <feather-icon v-else icon="DownloadIcon" size="16" class="mr-50" />
            {{ $t("Export") }}
          </b-button>
        </div>
      </div>
    </b-card-header>

    <!-- Period summary -->
    <div class="dash-att-overview">
      <div class="dash-att-overview__head">
        <span class="dash-att-overview__title">{{ $t("Day summary") }}</span>
        <span class="dash-att-overview__pill">
          <feather-icon icon="CalendarIcon" size="13" class="mr-25" />
          {{ formattedDate }}
        </span>
      </div>

      <div class="dash-att-progress-row">
        <div
          class="dash-att-progress"
          :title="`${markedPercent}% ${$t('Marked')}`"
        >
          <div
            class="dash-att-progress__fill"
            :style="{ width: `${markedPercent}%` }"
          >
            <span class="dash-att-progress__glow" aria-hidden="true" />
          </div>
        </div>
        <div class="dash-att-overview__meta">
          <span class="dash-att-overview__classes">
            <strong>{{ classCount }}</strong> {{ $t("Classes") }}
          </span>
          <span class="dash-att-overview__dot">·</span>
          <span class="dash-att-overview__marked">
            {{ $t("Marked") }}
            <strong>{{ markedPercent }}%</strong>
          </span>
        </div>
      </div>
    </div>

    <!-- Summary stats (same pattern as staffAttendance.vue) -->
    <div class="dash-att-metrics">
      <div
        v-if="attLoading"
        class="attendance-stats attendance-stats--skeleton"
      >
        <div
          v-for="n in 7"
          :key="`att-stat-skel-${n}`"
          class="attendance-stat-skeleton"
        >
          <div class="attendance-stat-skeleton__value" />
          <div class="attendance-stat-skeleton__label" />
        </div>
      </div>
      <div v-else class="attendance-stats">
        <div
          v-for="m in summaryMetrics"
          :key="m.key"
          class="attendance-stat"
          :class="'is-' + m.tone"
        >
          <span class="attendance-stat__code">{{ m.code }}</span>
          <span class="attendance-stat__label">{{ m.label }}</span>
          <span class="attendance-stat__value">{{ m.value }}</span>
        </div>
      </div>
    </div>

    <div class="dash-table-scroll dash-table-scroll--att">
    <b-table
        class="dash-att-table mb-0"
      show-empty
      :items="attItems"
      :fields="attFields"
      :foot-clone="true"
      hover
      :busy="attLoading"
        thead-class="dash-att-table__head"
        tbody-tr-class="dash-att-table__row"
    >
      <template #table-busy>
          <div class="dash-att-busy py-5">
            <b-spinner variant="primary" />
            <span class="ml-1 text-muted">{{ $t("Loading") }}...</span>
        </div>
      </template>

      <template #head()="scope">
          <span
            class="dash-att-th"
            :class="{ 'dash-att-th--class': scope.column === 'className' }"
          >
          {{ $t(scope.label) }}
          </span>
      </template>

      <template #cell(className)="data">
          <div class="dash-att-class-cell">
            <span class="dash-att-class-cell__name">{{ data.value }}</span>
            <span class="dash-att-class-cell__meta">
              {{ data.item.present || 0 }} {{ $t("present") }} ·
              {{ data.item.unmarked || 0 }} {{ $t("unmarked") }}
            </span>
          </div>
      </template>
      <template #cell(total)="data">
          <span class="dash-att-pill dash-att-pill--neutral">{{
            data.value
          }}</span>
      </template>
      <template #cell(present)="data">
          <span class="dash-att-pill dash-att-pill--success">{{
            data.value
          }}</span>
      </template>
      <template #cell(absent)="data">
          <span class="dash-att-pill dash-att-pill--danger">{{
            data.value
          }}</span>
      </template>
      <template #cell(leave)="data">
          <span class="dash-att-pill dash-att-pill--info">{{
            data.value
          }}</span>
      </template>
      <template #cell(late)="data">
          <span class="dash-att-pill dash-att-pill--warning">{{
            data.value
          }}</span>
      </template>
      <template #cell(holiday)="data">
          <span class="dash-att-pill dash-att-pill--holiday">{{
            data.value
          }}</span>
      </template>
      <template #cell(unmarked)="data">
          <span
            class="dash-att-pill"
            :class="
              Number(data.value) > 0
                ? 'dash-att-pill--unmarked-warn'
                : 'dash-att-pill--muted'
            "
          >
            {{ data.value }}
          </span>
        </template>

        <template #empty>
          <div class="dash-att-empty">
            <feather-icon icon="InboxIcon" size="32" class="text-muted mb-1" />
            <p class="mb-0 text-muted">{{ $t("No records found") }}</p>
          </div>
        </template>

        <template #foot()="scope">
          <span
            v-if="scope.column === 'className'"
            class="dash-att-th dash-att-th--foot"
            >{{ $t("Total") }}</span
          >
          <span
            v-else
            class="dash-att-pill"
            :class="`dash-att-pill--${footTone(scope.column) || 'neutral'}`"
            >{{ totalFor(scope.column) }}</span
          >
      </template>
    </b-table>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BButton,
  BSpinner,
  BTable,
  BCardHeader,
  BCardTitle,
  VBTooltip,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";

import { mapActions } from "vuex";

export default {
  name: "AttendanceCard",
  components: {
    BCard,
    BButton,
    BSpinner,
    BTable,
    BCardHeader,
    BCardTitle,
    flatPickr,
  },
  props: {
    campusID: {
      type: Number,
    },
    initialDate: {
      type: String,
      default: () => new Date().toJSON().split("T")[0],
    },
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      attDate: this.initialDate,
      config2: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      attSummary: {
        stdTotal: 0,
        stdPTotal: 0,
        stdATotal: 0,
        stdLvTotal: 0,
        stdLtTotal: 0,
        stdHTotal: 0,
        stdUnmarkedTotal: 0,
      },
      attLoading: false,
      attItems: [],
      exporting: false,
    };
  },
  computed: {
    isHappinessFoundationDomain() {
      return this.reportDomain === "myskoolhappinessfoundation";
    },
    classCount() {
      return (this.attItems && this.attItems.length) || 0;
    },
    formattedDate() {
      if (!this.attDate) return "";
      const d = new Date(`${this.attDate}T12:00:00`);
      if (Number.isNaN(d.getTime())) return this.attDate;
      return d.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    markedPercent() {
      const total = Number(this.attSummary.stdTotal) || 0;
      if (total <= 0) return 0;
      const unmarked = Number(this.attSummary.stdUnmarkedTotal) || 0;
      const marked = Math.max(0, total - unmarked);
      return Math.min(100, Math.round((marked / total) * 100));
    },
    summaryMetrics() {
      const s = this.attSummary;
      return [
        {
          key: "total",
          code: "Σ",
          label: this.$t("Total"),
          value: s.stdTotal,
          tone: "total",
        },
        {
          key: "p",
          code: "P",
          label: this.$t("Present"),
          value: s.stdPTotal,
          tone: "present",
        },
        {
          key: "a",
          code: "A",
          label: this.$t("Absent"),
          value: s.stdATotal,
          tone: "absent",
        },
        {
          key: "l",
          code: "L",
          label: this.$t("Leave"),
          value: s.stdLvTotal,
          tone: "leave",
        },
        {
          key: "late",
          code: "Late",
          label: this.$t("Late"),
          value: s.stdLtTotal,
          tone: "late",
        },
        {
          key: "h",
          code: "H",
          label: this.$t("Holiday"),
          value: s.stdHTotal,
          tone: "holiday",
        },
        {
          key: "u",
          code: "—",
          label: this.$t("Unmarked"),
          value: s.stdUnmarkedTotal,
          tone: "unmarked",
        },
      ];
    },
    attFields() {
      const classLabel = this.isHappinessFoundationDomain ? "Group" : "Class";
      const numTh = "dash-att-td-num text-center";
      const numTd = "dash-att-td-num text-center align-middle";
      return [
        {
          label: classLabel,
          key: "className",
          thClass: "dash-att-td-class",
          tdClass: "dash-att-td-class align-middle",
        },
        { label: "Students", key: "total", thClass: numTh, tdClass: numTd },
        { label: "Present", key: "present", thClass: numTh, tdClass: numTd },
        { label: "Absent", key: "absent", thClass: numTh, tdClass: numTd },
        { label: "Leave", key: "leave", thClass: numTh, tdClass: numTd },
        { label: "Late", key: "late", thClass: numTh, tdClass: numTd },
        { label: "Holiday", key: "holiday", thClass: numTh, tdClass: numTd },
        {
          label: "Unmarked",
          key: "unmarked",
          thClass: numTh,
          tdClass: numTd,
        },
      ];
    },
  },
  created() {
    this.LoadData();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    /** Pill tone for the summary footer, keyed by column. */
    footTone(column) {
      const tones = {
        total: "neutral",
        present: "success",
        absent: "danger",
        leave: "info",
        late: "warning",
        holiday: "holiday",
        unmarked: "unmarked-warn",
      };
      return tones[column] || "";
    },
    /** Totals row — mirrors the metric tiles. */
    totalFor(column) {
      const s = this.attSummary || {};
      const totals = {
        total: s.stdTotal,
        present: s.stdPTotal,
        absent: s.stdATotal,
        leave: s.stdLvTotal,
        late: s.stdLtTotal,
        holiday: s.stdHTotal,
        unmarked: s.stdUnmarkedTotal,
      };
      return Number(totals[column]) || 0;
    },
    /** Percentage of a class that already has attendance marked. */
    rowMarked(row) {
      if (!row) return 0;
      const total = Number(row.total) || 0;
      if (total <= 0) return 0;
      const unmarked = Number(row.unmarked) || 0;
      const marked = Math.max(0, total - unmarked);
      return Math.min(100, Math.round((marked / total) * 100));
    },

    sortHappinessClassRows(items) {
      const sectionOrder = {
        oa: 1,
        ob: 2,
        "level 1": 3,
        "level 2": 4,
      };

      const parseClassName = (className) => {
        const str = String(className || "").trim();
        const match = str.match(/^(\d+)\s*[-–]\s*(.+)$/i);
        if (match) {
          return {
            grade: parseInt(match[1], 10) || 0,
            section: match[2].trim().toLowerCase(),
            raw: str.toLowerCase(),
          };
        }
        return { grade: 9999, section: str.toLowerCase(), raw: str.toLowerCase() };
      };

      return [...items].sort((a, b) => {
        const pa = parseClassName(a.className);
        const pb = parseClassName(b.className);
        if (pa.grade !== pb.grade) return pa.grade - pb.grade;

        const orderA = sectionOrder[pa.section] ?? 99;
        const orderB = sectionOrder[pb.section] ?? 99;
        if (orderA !== orderB) return orderA - orderB;

        return pa.raw.localeCompare(pb.raw, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    },

    async exportData() {
      const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");
      this.exporting = true;

      const classCol = this.isHappinessFoundationDomain ? "group" : "class";
      const expData = this.attItems.map((item) => ({
        [classCol]: item.className,
        students: item.total,
        present: item.present,
        absent: item.absent,
        leave: item.leave,
        late: item.late,
        holiday: item.holiday,
      }));

      expData.push({
        [classCol]: "",
        students: "",
        present: "",
        absent: "",
        leave: "",
        late: "",
        holiday: "",
      });
      expData.push({
        [classCol]: "Total",
        students: this.attSummary.stdTotal,
        present: this.attSummary.stdPTotal,
        absent: this.attSummary.stdATotal,
        leave: this.attSummary.stdLvTotal,
        late: this.attSummary.stdLtTotal,
        holiday: this.attSummary.stdHTotal,
      });

      const filename = `attendance ${this.attDate}.xlsx`;
      const worksheet = XLSX.utils.json_to_sheet(expData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      XLSX.writeFile(workbook, filename);
      this.exporting = false;
    },
    async LoadData() {
      if (!this.attDate) return;
        this.attLoading = true;
      try {
        const campusId = this.campusID || this.$store.state.userData.cId;

        var obj = {
          url:
            this.$store.state.domain +
            "Users/DashboardClasswiseAttendance?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            campusId +
            "&dt=" +
            this.attDate,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = (await this.get(obj)) || {};
        // `get` resolves to [] on transport/API errors — keep the last good
        // rows instead of throwing on a missing payload.
        this.attItems = this.isHappinessFoundationDomain
          ? this.sortHappinessClassRows(res.data || [])
          : res.data || [];
        this.attSummary = {
          stdTotal: res.stdTotal,
          stdPTotal: res.stdPTotal,
          stdATotal: res.stdATotal,
          stdLvTotal: res.stdLvTotal,
          stdLtTotal: res.stdLtTotal,
          stdHTotal: res.stdHTotal,
          stdUnmarkedTotal: res.stdUnmarkedTotal,
        };
      } finally {
        this.attLoading = false;
      }
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.dash-panel__icon--att {
  background: linear-gradient(
    135deg,
    rgba(237, 233, 254, 0.95) 0%,
    rgba(219, 234, 254, 0.95) 100%
  );
  color: #7367f0;
  border: 1px solid rgba(115, 103, 240, 0.12);
}

.dash-att-panel--glass {
  border-radius: 20px !important;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.52) !important;
  backdrop-filter: blur(16px) saturate(1.12);
  -webkit-backdrop-filter: blur(16px) saturate(1.12);
  border: 1px solid rgba(255, 255, 255, 0.78) !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 14px 36px rgba(115, 103, 240, 0.07) !important;
}

.dash-att-panel__header {
  padding-bottom: 0.65rem !important;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0) 100%
  ) !important;
}

.dash-att-panel__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
}

.dash-att-panel__actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.dash-att-date {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.4rem 0.85rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.06);
  color: #64748b;
  cursor: pointer;
}

.dash-att-date__input {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-width: 6.5rem;
  font-weight: 700;
  font-size: 0.85rem;
  color: #1e293b !important;
  cursor: pointer;
}

.dash-att-export {
  border-radius: 12px !important;
  font-weight: 700;
  font-size: 0.82rem;
  padding-left: 0.85rem !important;
  padding-right: 0.95rem !important;
}

.dash-att-overview {
  padding: 0.85rem 1.15rem 1rem;
  border-bottom: 1px solid rgba(115, 103, 240, 0.08);
  background: rgba(255, 255, 255, 0.28);
}

.dash-att-overview__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.6rem;
}

.dash-att-overview__title {
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: #334155;
}

.dash-att-overview__pill {
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.65rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(115, 103, 240, 0.12);
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.dash-att-progress-row {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.dash-att-overview__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem 0.45rem;
  font-size: 0.78rem;
  color: #64748b;
  white-space: nowrap;
  margin-left: auto;

  strong {
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }
}

.dash-att-overview__dot {
  opacity: 0.45;
}

.dash-att-progress {
  position: relative;
  flex: 1 1 180px;
  height: 8px;
  border-radius: 999px;
  background: rgba(226, 232, 240, 0.75);
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.05);
}

.dash-att-progress__fill {
  position: relative;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #34d399 0%, #10b981 55%, #059669 100%);
  transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
}

.dash-att-progress__glow {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: translateX(-100%);
  animation: dashAttShine 2.4s ease-in-out infinite;
}

@keyframes dashAttShine {
  0% {
    transform: translateX(-100%);
  }
  60%,
  100% {
    transform: translateX(100%);
  }
}

/* ── Summary stats (staffAttendance / _attendance-ui.scss) ─────── */
.dash-att-metrics {
  padding: 0.9rem 1.15rem 1.05rem;
  border-bottom: 1px solid rgba(115, 103, 240, 0.08);
  background: rgba(255, 255, 255, 0.22);
}

.dash-att-metrics .attendance-stats {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.65rem;
  margin-bottom: 0;
}

@media (max-width: 1199px) {
  .dash-att-metrics .attendance-stats {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .dash-att-metrics .attendance-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.dash-att-metrics .attendance-stat {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 92px;
  padding: 0.6rem 0.75rem 0.65rem;
  border-radius: 12px;
  border: 1px solid transparent;
  color: #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  min-width: 0;
}

.dash-att-metrics .attendance-stat__code {
  position: absolute;
  top: 0.5rem;
  right: 0.55rem;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.2px;
  padding: 0.15rem 0.4rem;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  line-height: 1.2;
}

.dash-att-metrics .attendance-stat__label {
  display: block;
  padding-right: 2.25rem;
  min-height: 2.35em;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.25;
}

.dash-att-metrics .attendance-stat__value {
  display: block;
  margin-top: 0.25rem;
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1;
  color: #fff;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.dash-att-metrics .attendance-stat.is-total {
  background: linear-gradient(145deg, #a78bfa 0%, #7c3aed 100%);
}
.dash-att-metrics .attendance-stat.is-present {
  background: linear-gradient(145deg, #32d583 0%, #28c76f 100%);
}
.dash-att-metrics .attendance-stat.is-absent {
  background: linear-gradient(145deg, #f08182 0%, #ea5455 100%);
}
.dash-att-metrics .attendance-stat.is-leave {
  background: linear-gradient(145deg, #22d3ee 0%, #00cfe8 100%);
}
.dash-att-metrics .attendance-stat.is-late {
  background: linear-gradient(145deg, #ffb366 0%, #ff9f43 100%);
}
.dash-att-metrics .attendance-stat.is-holiday {
  background: linear-gradient(145deg, #38bdf8 0%, #0ea5e9 100%);
}
.dash-att-metrics .attendance-stat.is-unmarked {
  background: linear-gradient(145deg, #94a3b8 0%, #64748b 100%);
}

.dash-att-metrics .attendance-stats--skeleton {
  pointer-events: none;
}

.dash-att-metrics .attendance-stat-skeleton {
  min-height: 92px;
  padding: 0.65rem 0.75rem;
  border-radius: 12px;
  border: none;
  background: #e2e8f0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 0.45rem;
  opacity: 0.65;
}

.dash-att-metrics .attendance-stat-skeleton__value {
  width: 42%;
  height: 1.65rem;
  border-radius: 8px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 40%, #e2e8f0 80%);
  background-size: 200% 100%;
  animation: dashAttSk 1.2s ease-in-out infinite;
}

.dash-att-metrics .attendance-stat-skeleton__label {
  width: 72%;
  height: 0.75rem;
  border-radius: 6px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 40%, #e2e8f0 80%);
  background-size: 200% 100%;
  animation: dashAttSk 1.2s ease-in-out infinite;
}

@keyframes dashAttSk {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.dash-table-scroll--att {
  max-height: min(480px, 62vh);
  border-top: none;
  background: rgba(255, 255, 255, 0.18);
  scrollbar-width: thin;
  scrollbar-color: rgba(115, 103, 240, 0.35) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: linear-gradient(180deg, #b9b3f8, #7367f0);
  }
}

.dash-att-table {
  font-size: 0.84rem;
  background: transparent;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: rgba(248, 250, 252, 0.92) !important;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(115, 103, 240, 0.1) !important;
    border-top: none !important;
    padding: 0.6rem 0.75rem !important;
    vertical-align: middle;
    box-shadow: 0 1px 0 rgba(115, 103, 240, 0.06);
  }

  tbody td {
    padding: 0.5rem 0.75rem !important;
    vertical-align: middle;
    border-color: rgba(241, 245, 249, 0.95) !important;
    background: transparent;
  }

  tbody tr {
    transition: background 0.15s ease;
  }

  tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.35);
  }

  tbody tr:hover {
    background: rgba(237, 233, 254, 0.35) !important;
  }

  tfoot td {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: rgba(248, 250, 252, 0.92) !important;
    backdrop-filter: blur(10px);
    border-top: 1px solid rgba(115, 103, 240, 0.1) !important;
    padding: 0.55rem 0.75rem !important;
    box-shadow: 0 -1px 0 rgba(115, 103, 240, 0.06);
  }
}

.dash-att-th {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;

  &--class {
    text-align: left;
  }

  &--foot {
    font-size: 0.72rem;
    color: #334155;
  }
}

.dash-att-td-class {
  min-width: 140px;
  max-width: 220px;
}

.dash-att-td-num {
  width: 4.5rem;
}

.dash-att-class-cell {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  min-width: 0;
  padding-left: 0.1rem;

  &__name {
    font-weight: 700;
    font-size: 0.8125rem;
    color: #1e293b;
    line-height: 1.3;
    word-break: break-word;
  }

  &__meta {
    font-size: 0.65rem;
    font-weight: 600;
    color: #94a3b8;
    white-space: nowrap;
  }
}

.dash-att-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;

  &--neutral {
    background: rgba(241, 245, 249, 0.95);
    color: #475569;
    border: 1px solid rgba(226, 232, 240, 0.9);
  }
  &--success {
    background: rgba(220, 252, 231, 0.95);
    color: #15803d;
    border: 1px solid rgba(134, 239, 172, 0.45);
  }
  &--danger {
    background: rgba(254, 226, 226, 0.95);
    color: #b91c1c;
    border: 1px solid rgba(252, 165, 165, 0.45);
  }
  &--warning {
    background: rgba(254, 243, 199, 0.95);
    color: #b45309;
    border: 1px solid rgba(253, 224, 71, 0.4);
  }
  &--info {
    background: rgba(207, 250, 254, 0.95);
    color: #0e7490;
    border: 1px solid rgba(103, 232, 249, 0.45);
  }
  &--holiday {
    background: rgba(224, 242, 254, 0.95);
    color: #0369a1;
    border: 1px solid rgba(125, 211, 252, 0.45);
  }
  &--muted {
    background: rgba(248, 250, 252, 0.95);
    color: #94a3b8;
    border: 1px solid rgba(226, 232, 240, 0.9);
  }
  &--unmarked-warn {
    background: rgba(254, 243, 199, 0.95);
    color: #92400e;
    border: 1px solid rgba(251, 191, 36, 0.45);
  }
}

.dash-att-busy {
  display: flex;
  align-items: center;
  justify-content: center;
}

.dash-att-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
}
</style>
